﻿import { Tab } from "@headlessui/react";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { AddDeviceFormType } from "@/apps/partner/types";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { RadioButton } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { RateCardGrid } from "@/components/Layout/Rates/RateCard/RateCardGrid";
import { RateTariffHint } from "@/components/Layout/Rates/RateCard/RateTariffHint";
import {
  getDataOnlyProducts,
  getFlatHomeProductsWithFallback,
  getMobileAbos,
  getSortedMobileAbosMonthly,
} from "@/components/Layout/Rates/rateSelection.utils";
import { RateTypeTabs } from "@/components/Layout/Rates/RateTypeTabs/RateTypeTabs";
import { useGetOptions } from "@/services/api";
import {
  isFlatHomeTariff,
  isFlatMobileTariff,
  TransformedProduct,
  transformProducts,
} from "@/utils/tariffUtils";

type RateFormProps = {
  rate: string;
};

const PartnerRateCardSection = ({
  mobileAbos,
  dataOnlyProducts,
  flatHomeProducts,
  rateTypeTabIndex,
  onRateTypeTabIndexChange,
  disableRateTypeSwitch,
  hideSecondTab,
  onRateChange,
  isUsingTabbedSwitch,
  nonTabbedProducts,
}: {
  mobileAbos: TransformedProduct[];
  dataOnlyProducts: TransformedProduct[];
  flatHomeProducts: TransformedProduct[];
  rateTypeTabIndex: number;
  onRateTypeTabIndexChange: (index: number) => void;
  disableRateTypeSwitch: boolean;
  hideSecondTab: boolean;
  onRateChange: (rate: string) => void;
  isUsingTabbedSwitch: boolean;
  nonTabbedProducts: TransformedProduct[];
}) => {
  const { t } = useTranslation();
  const rateFormMethods = useForm<RateFormProps>({
    defaultValues: { rate: "" },
  });

  const parentForm = useFormContext<AddDeviceFormType>();
  const currentRate = parentForm.watch("productId");
  const showFlatHomeTab = flatHomeProducts.length > 0;
  const tabCount = showFlatHomeTab ? 3 : 2;
  const safeRateTypeTabIndex = Math.min(rateTypeTabIndex, tabCount - 1);

  useEffect(() => {
    if (currentRate) {
      rateFormMethods.setValue("rate", String(currentRate));
    }
  }, [currentRate, rateFormMethods]);

  const handleRateChange = (rate: string) => {
    rateFormMethods.setValue("rate", rate);
    onRateChange(rate);
  };

  const rateGrid = (
    ariaLabel: string,
    products: TransformedProduct[],
    hint?: ReactNode,
  ) => (
    <RateCardGrid
      ariaLabel={ariaLabel}
      value={rateFormMethods.watch("rate")}
      products={products}
      hint={hint}
      onChange={handleRateChange}
    />
  );

  if (!isUsingTabbedSwitch) {
    return (
      <FormProvider {...rateFormMethods}>
        <CardContainer
          className="flex w-full flex-col rounded-lg p-4"
          variant="white"
        >
          {rateGrid(
            t("partnerPortal:addDevice.rateSelection.label"),
            nonTabbedProducts,
            <RateTariffHint />,
          )}
        </CardContainer>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...rateFormMethods}>
      <CardContainer
        className="flex w-full flex-col rounded-lg pb-2"
        variant="white"
      >
        <RateTypeTabs
          selectedIndex={safeRateTypeTabIndex}
          onIndexChange={onRateTypeTabIndexChange}
          listClassName="px-4 pb-2 pt-4"
          disabled={disableRateTypeSwitch}
          hideSecondTab={hideSecondTab}
          firstLabel={t("partnerPortal:addDevice.rateSelection.flatMobile")}
          secondLabel={t("partnerPortal:addDevice.rateSelection.dataTariffs")}
          thirdLabel={
            showFlatHomeTab
              ? t("partnerPortal:addDevice.rateSelection.flatHome")
              : undefined
          }
        >
          <Tab.Panels className="flex flex-col gap-6 px-4 py-2">
            <Tab.Panel>
              {rateGrid(
                t("partnerPortal:addDevice.rateSelection.flatMobile"),
                getSortedMobileAbosMonthly(mobileAbos),
              )}
            </Tab.Panel>
            <Tab.Panel>
              {rateGrid(
                t("partnerPortal:addDevice.rateSelection.dataTariffs"),
                dataOnlyProducts,
                <RateTariffHint />,
              )}
            </Tab.Panel>
            {showFlatHomeTab && (
              <Tab.Panel>
                {rateGrid(
                  t("partnerPortal:addDevice.rateSelection.flatHome"),
                  flatHomeProducts,
                )}
              </Tab.Panel>
            )}
          </Tab.Panels>
        </RateTypeTabs>
      </CardContainer>
    </FormProvider>
  );
};

export const RateSelection: FC = () => {
  const { t } = useTranslation();
  const {
    watch,
    setValue,
    register,
    formState: { errors },
  } = useFormContext<AddDeviceFormType>();

  const [rateTypeTabIndex, setRateTypeTabIndex] = useState(0);

  const currentRate = watch("productId");
  const onp = watch("onp");

  const { data: availableOptions, isFetching: isLoadingOptions } =
    useGetOptions(131001, undefined, {
      query: {
        refetchOnWindowFocus: false,
      },
    });

  const options = useMemo(
    () => availableOptions && transformProducts(availableOptions),
    [availableOptions],
  );
  const { processedProductsById } = options || {};

  const flatHomeProducts = useMemo(
    () => getFlatHomeProductsWithFallback(processedProductsById),
    [processedProductsById],
  );

  const selectedOption: TransformedProduct | undefined = useMemo(() => {
    if (!currentRate) {
      return undefined;
    }

    return (
      processedProductsById?.[currentRate] ??
      flatHomeProducts.find((product) => product.id === currentRate)
    );
  }, [currentRate, flatHomeProducts, processedProductsById]);

  const mobileAbos = useMemo(
    () => getMobileAbos(processedProductsById),
    [processedProductsById],
  );

  const dataOnlyProducts = useMemo(
    () => getDataOnlyProducts(processedProductsById),
    [processedProductsById],
  );

  const nonTabbedProducts = useMemo(
    () => [...dataOnlyProducts, ...flatHomeProducts],
    [dataOnlyProducts, flatHomeProducts],
  );

  const monthlyMobileAbos = useMemo(
    () => getSortedMobileAbosMonthly(mobileAbos),
    [mobileAbos],
  );

  const showFlatHomeTab = flatHomeProducts.length > 0;
  const tabCount = showFlatHomeTab ? 3 : 2;

  const isFlatMobileSelected =
    selectedOption?.baseDisplayValue &&
    isFlatMobileTariff(selectedOption.baseDisplayValue);

  const prechargedProduct = availableOptions?.some(
    (product) => product.precharged,
  );
  const isUsingTabbedSwitch = mobileAbos.length > 0;
  const hideSecondTab = onp === "true";

  const handleRateChange = (rate: string) => {
    setValue("productId", Number(rate), { shouldValidate: false });
  };

  const handleRateTypeTabIndexChange = (index: number) => {
    setRateTypeTabIndex(index);
    setValue("productId", undefined as unknown as number, {
      shouldValidate: false,
    });
    setValue("tariffName", "", { shouldValidate: false });
    setValue("tariffPrice", "", { shouldValidate: false });
    setValue("tariffSpeed", "", { shouldValidate: false });
  };

  useEffect(() => {
    if (!selectedOption) {
      return;
    }

    setValue("tariffName", selectedOption.baseDisplayValue || "", {
      shouldValidate: false,
    });
    setValue("tariffPrice", selectedOption.chargeFlat || "", {
      shouldValidate: false,
    });
    setValue("tariffSpeed", selectedOption.shortName || "", {
      shouldValidate: false,
    });
  }, [selectedOption, setValue]);

  useEffect(() => {
    if (!isUsingTabbedSwitch || !selectedOption?.baseDisplayValue) {
      return;
    }

    if (isFlatMobileTariff(selectedOption.baseDisplayValue)) {
      setRateTypeTabIndex(0);
    } else if (isFlatHomeTariff(selectedOption.id)) {
      setRateTypeTabIndex(showFlatHomeTab ? 2 : 1);
    } else {
      setRateTypeTabIndex(1);
    }
  }, [isUsingTabbedSwitch, selectedOption, showFlatHomeTab]);

  useEffect(() => {
    if (!isUsingTabbedSwitch || isLoadingOptions) {
      return;
    }

    const tabProducts = [
      monthlyMobileAbos,
      dataOnlyProducts,
      ...(showFlatHomeTab ? [flatHomeProducts] : []),
    ];
    const clampedIndex = Math.min(rateTypeTabIndex, tabProducts.length - 1);

    if (tabProducts[clampedIndex]?.length > 0) {
      return;
    }

    const firstNonEmptyTabIndex = tabProducts.findIndex(
      (products) => products.length > 0,
    );

    if (firstNonEmptyTabIndex >= 0) {
      setRateTypeTabIndex(firstNonEmptyTabIndex);
    }
  }, [
    dataOnlyProducts,
    flatHomeProducts,
    isLoadingOptions,
    isUsingTabbedSwitch,
    monthlyMobileAbos,
    rateTypeTabIndex,
    showFlatHomeTab,
  ]);

  useEffect(() => {
    if (selectedOption && !isFlatMobileSelected) {
      setValue("onp", "false", { shouldValidate: false });
    }
  }, [selectedOption, isFlatMobileSelected, setValue]);

  return (
    <div
      id="partner-rate-selection"
      data-testid="partner-rate-selection"
      className="flex w-full flex-col gap-6"
    >
      <div id="rate-section" className="flex flex-col gap-6">
        {isLoadingOptions && <LoadingSpinner />}

        {errors.productId && !currentRate && (
          <span className="text-body-sm text-foreground-error">
            {String(errors.productId.message)}
          </span>
        )}

        <PartnerRateCardSection
          mobileAbos={mobileAbos}
          dataOnlyProducts={dataOnlyProducts}
          flatHomeProducts={flatHomeProducts}
          rateTypeTabIndex={Math.min(rateTypeTabIndex, tabCount - 1)}
          onRateTypeTabIndexChange={handleRateTypeTabIndexChange}
          disableRateTypeSwitch={!!prechargedProduct}
          hideSecondTab={hideSecondTab}
          onRateChange={handleRateChange}
          isUsingTabbedSwitch={isUsingTabbedSwitch}
          nonTabbedProducts={nonTabbedProducts}
        />

        {prechargedProduct && (
          <p className="text-body-xs text-foreground-secondary">
            {t(
              "translation:Onboarding.sections.rate-selection.prechargedRateHint",
            )}
          </p>
        )}

        {isFlatMobileSelected && (
          <fieldset className="flex flex-col gap-4">
            <legend className="text-body-base font-semibold text-foreground-primary">
              {t("Onboarding.sections.number-porting.title")}
            </legend>
            <div className="flex gap-6">
              <RadioButton
                label={t("partnerPortal:addDevice.onp.no")}
                name="onp"
                value="false"
                errors={errors}
                register={register}
              />
              <RadioButton
                label={t("partnerPortal:addDevice.onp.yes")}
                name="onp"
                value="true"
                errors={errors}
                register={register}
              />
            </div>
            {onp === "true" && (
              <p className="text-body-sm text-foreground-secondary">
                {t("partnerPortal:addDevice.onp.yesHint")}
              </p>
            )}
          </fieldset>
        )}
      </div>

      <div id="device-name">
        <FormField
          id="device-name"
          label={t("Common.label.subscriptionName")}
          name="simAlias"
          errors={errors}
          register={register}
          type="text"
          placeholder={t("Common.label.subscriptionName")}
          options={{
            required: t("translation:label.validation.required"),
          }}
        />
      </div>
    </div>
  );
};
